import { Grid } from 'theme-ui';
import EmailSignupForm from './Form';
import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import ModalHeadings from './ModalHeadings';
import ModalBody from './ModalBody';
import CloseButton from './CloseButton';
import ModalImage from './ModalImage';
import AnimatedModal from '../Modal/AnimatedModal';
import { useSessionStorage } from '~/hooks/components/use-storage';
import { useLocation } from '@reach/router';
import useShouldShowModal from '~/hooks/components/use-should-show-modal';

type Props = {
  modal: Queries.ModalFragmentFragment;
  includeModalPaths: string[];
};

const EmailSignup = ({ modal, includeModalPaths }: Props) => {
  const { value: isClosedByUser, setValue: setIsClosedByUser } = useSessionStorage('isClosedByUser', false);
  const { pathname } = useLocation();
  const shouldShowSiteWideModal = useShouldShowModal({ includeModalPaths, modal, pathname });
  const [isOpen, setIsOpen] = useState(false);
  const isSubmittedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    heading,
    headingMobile,
    subheading,
    subheadingMobile,
    body,
    bodyMobile,
    image,
    imageMobile,
    showEmailInputField,
    showDoBInputField,
    showNameInputFields,
    emailFieldPlaceholder,
    confirmationMessage,
    delayBeforeShow,
    dismissMessage,
    closeButtonColor,
  } = modal;

  useEffect(() => {
    const submittedFromLocalStorage = localStorage.getItem('isSubmitted');
    if (submittedFromLocalStorage) {
      isSubmittedRef.current = JSON.parse(submittedFromLocalStorage);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('isSubmitted', JSON.stringify(isSubmittedRef.current));
  }, []);

  const handleScroll = useCallback(() => {
    const delay = delayBeforeShow ? delayBeforeShow * 1000 : 1000;
    if (isSubmittedRef.current || isClosedByUser || isOpen || !shouldShowSiteWideModal) return;

    timeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, delay);
  }, [delayBeforeShow, isClosedByUser, isOpen, shouldShowSiteWideModal]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { once: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const handleClose = () => {
    setIsOpen(false);
    setIsClosedByUser(true);
  };

  const isInformationalModal = !showEmailInputField && !showDoBInputField && !showNameInputFields;

  return (
    <AnimatedModal
      ariaLabel="Email Signup Modal"
      bypassScrollLock={true}
      bypassFocusLock={true}
      isOpen={isOpen}
      onDismiss={handleClose}
      closeButtonColor={closeButtonColor}
      forwardSx={{ backgroundColor: 'transparent' }}
    >
      <Grid
        key="modal-grid"
        sx={{
          overflow: 'auto',
          backgroundColor: 'white',
          rowGap: '1.6rem',
          columnGap: 0,
          alignItems: 'center',
          position: 'relative',
          maxWidth: '96rem',
          maxHeight: '100vh',
          gridTemplateColumns: ['1fr', '3fr 2fr'],
          gridTemplateAreas: isInformationalModal
            ? [
                `
            "headings"
            "image"
            "body"
            `,
                `
            "headings image"
            "body image"
            `,
              ]
            : [
                `
            "headings"
            "image"
            "form"
            "body"
            `,
                `
            "headings image"
            "form image"
            "body image"
          `,
              ],
        }}
      >
        <CloseButton handleClose={handleClose} />
        <ModalHeadings
          heading={heading}
          headingMobile={headingMobile}
          subheading={subheading}
          subheadingMobile={subheadingMobile}
          forwardSx={{ gridArea: 'headings' }}
        />

        <ModalBody
          body={body}
          bodyMobile={bodyMobile}
          forwardSx={{
            gridArea: 'body',

            ...(isInformationalModal ? { pb: '3.2rem' } : {}),
          }}
          handleClose={handleClose}
          dismissMessage={dismissMessage}
        />
        {!isInformationalModal && (
          <EmailSignupForm
            placement="modal"
            forwardSx={{ px: '2.4rem', gridArea: 'form' }}
            theme="light"
            placeholder={emailFieldPlaceholder}
            setIsOpen={setIsOpen}
            showEmailInputField={showEmailInputField}
            showDoBInputField={showDoBInputField}
            showNameInputFields={showNameInputFields}
            confirmationMessage={confirmationMessage}
            isSubmitted={isSubmittedRef}
          />
        )}
        <ModalImage image={image} imageMobile={imageMobile} forwardSx={{ gridArea: 'image' }} />
      </Grid>
    </AnimatedModal>
  );
};

export default memo(EmailSignup);
